class Collection {
  constructor() {
    this.selectors = {
      section: ".facest-filters-section",
      toolbar: ".m-collection-toolbar",
      cols: [".m-toolbar--column-switcher button"],
      productContainer: "[data-product-container]",
      loadMoreBtn: "[data-load-more]",
      // Sidebar filters selectors
      sidebar: ".m-sidebar",
      sidebarContent: ".m-sidebar--content",
      openSidebar: ".m-sidebar--open",
      closeSidebar: ".m-sidebar--close",
      filtersWrapper: ".m-filter--wrapper",
    };
    this.mobileSortingSelectors = {
      openSorting: ".m-sortby--open",
      closeSorting: ".m-sortby-mobile--close",
      sortingWrapper: ".m-sortby-mobile--wrapper",
      sortingList: ".m-sortby-mobile--content",
    };
    this.sectionId = "";
    this.isDesignMode = MinimogSettings.design_mode;

    this.currentPage = 1;
    this.paginationType; // "paginate" | "loadmore" | "infinite"
    this.totalPages;
    this.totalProducts;
    this.infiniteLoadingObserver;

    this.showColSwitchers;
    this.cachedCol;
    this.initialCol;
    this.activeCol;
    this.STORAGE_KEY = "gridColumnViews";
    this.MAX_COL_BY_SCREEN_SIZE = {
      mobile: 2,
      tablet: 4,
      desktop: 5,
      wide: 5,
    };

    this.enableFilters;
    this.enableSorting;
    this.filtersType; // "storefront_filters" | "tags_filter"
    this.filtersPosition; // "leftColumn" | "rightColumn" | "fixed"
    this.accordions;
    this.sideEffectEventsAdded = false;
  }

  init = () => {
    this.infiniteLoadingObserver?.disconnect();
    this.prefetchObserver?.disconnect();
    this.loadingMore = false;
    this.restoringHistory = false;
    this.domNodes = queryDomNodes(this.selectors);
    this.mbSortingNodes = queryDomNodes(this.mobileSortingSelectors);
    if (!this.domNodes.section) return;
    this.setData();
    this.initGridView();
    this.initFilters();
    this.initMobileSorting();
    this.initProducts();
    // Handle sticky toolbar on mobile
    this.handleStickyToolbar();
    if (!this.sideEffectEventsAdded) {
      MinimogEvents.subscribe('ON_PRODUCT_LIST_UPDATED', () => { if (typeof refreshProductReview === 'function') refreshProductReview(); });
      document.addEventListener('matchMobile', () => this.handleStickyToolbar());
      document.addEventListener('unmatchMobile', () => this.handleStickyToolbar());
      document.addEventListener('click', (event) => {
        const link = event.target.closest('a[href*="/products/"]');
        if (link?.closest('[data-product-container]')) this.saveHistory();
      });
      window.addEventListener('pagehide', () => this.saveHistory());
      ['wheel', 'touchstart', 'pointerdown', 'keydown'].forEach(type => {
        window.addEventListener(type, () => { this.cancelHistoryScroll = true; }, { passive: true });
      });
    }
    this.sideEffectEventsAdded = true;
    this.restoreHistory();
  };

  saveHistory = () => {
    if (this.restoringHistory || !this.domNodes.productContainer?.isConnected) return;
    history.replaceState({ ...history.state, kpCollection: {
      url: location.pathname + location.search, page: this.currentPage, scroll: window.scrollY,
    } }, '', location.href);
  };

  restoreHistory = async () => {
    if (!this.domNodes.productContainer) return;
    const saved = window.KpListContext?.consumeRestore() || history.state?.kpCollection;
    if (!saved || saved.url !== location.pathname + location.search) return;
    const section = this.domNodes.section;
    this.restoringHistory = true;
    this.cancelHistoryScroll = false;
    const targetPage = Math.min(Number(saved.page) || 1, this.totalPages || 1);
    while (this.currentPage < targetPage && this.domNodes.section === section && !this.cancelHistoryScroll) {
      const previousPage = this.currentPage;
      await this.loadMoreProducts();
      if (this.currentPage === previousPage) break; // Keep the retry button usable on failure.
    }
    if (this.domNodes.section === section) {
      this.restoringHistory = false;
      if (!this.cancelHistoryScroll && Number.isFinite(saved.scroll)) {
        requestAnimationFrame(() => {
          if (!this.cancelHistoryScroll) window.scrollTo({ top: saved.scroll, behavior: 'instant' });
        });
      }
    }
  };

  setData = () => {
    const { section, productContainer } = this.domNodes;
    // Grid view data
    this.sectionId = section.dataset.sectionId;
    this.paginationType = section.dataset.paginationType;
    this.totalPages = productContainer && Number(productContainer.dataset.totalPages);
    this.view = section.dataset.view;
    // TODO: get from URL Search Params
    this.activeCol = null;
    this.currentPage = Math.max(1, Number(new URLSearchParams(location.search).get('page')) || 1);
    this.initialCol = Number(section.dataset.initialColumn);
    this.cachedCol = this.initialCol;
    this.showColSwitchers = section.dataset.showColSwitchers === "true";
    // Filters data
    this.enableFilters = section.dataset.enableFilters === "true";
    this.enableSorting = section.dataset.enableSorting === "true";
    this.filtersType = section.dataset.filtersType;
    this.filtersPosition = section.dataset.filtersPosition;
  };

  initGridView = () => {
    this.updateViewByScreen();
    // Collections with the toolbar switched off (bestsellers) have no column switcher; without this guard the
    // throw stopped init() before products and load-more were wired.
    if (this.domNodes.toolbar) addEventDelegate({
      selector: this.selectors.cols[0],
      context: this.domNodes.toolbar,
      handler: (e, colNode) => this.toggleView(Number(colNode.dataset.column), true),
    });
    this.initLoadMore();
    if (!this.sideEffectEventsAdded) {
      window.addEventListener("resize", () => {
        if (this.gridResizeFrame) return;
        this.gridResizeFrame = requestAnimationFrame(() => {
          this.gridResizeFrame = null;
          this.updateViewByScreen();
        });
      });
    }
  };

  initFilters = () => {
    if (!this.enableFilters) return;
    const { sidebar, openSidebar, closeSidebar } = this.domNodes;
    window.requestAnimationFrame(this.initAccordions);
    openSidebar?.addEventListener("click", this.openSidebarFilter);
    closeSidebar?.addEventListener("click", this.closeSidebarFilter);
    sidebar?.addEventListener("click", (e) => e.target === sidebar && this.closeSidebarFilter());
  };

  initMobileSorting = () => {
    if (!this.enableSorting) return;
    const { openSorting, closeSorting, sortingWrapper } = this.mbSortingNodes;
    openSorting?.addEventListener("click", this.openMobileSorting);
    closeSorting?.addEventListener("click", this.closeMobileSorting);
    sortingWrapper?.addEventListener("click", (e) => e.target === sortingWrapper && this.closeMobileSorting());
  };

  openMobileSorting = () => {
    const { sortingWrapper, sortingList } = this.mbSortingNodes;
    this.trapPanel(sortingList, this.closeMobileSorting);
    sortingWrapper.style.display = "block";
    window.requestAnimationFrame(() => {
      sortingWrapper.style.setProperty("--m-bg-opacity", "0.5");
      sortingList.style.setProperty("--m-translate-y", "0");
    });
  };

  closeMobileSorting = (e) => {
    const { sortingWrapper, sortingList } = this.mbSortingNodes;
    this.releasePanel();
    sortingWrapper.style.setProperty("--m-bg-opacity", "0");
    sortingList.style.setProperty("--m-translate-y", "100%");
    setTimeout(() => sortingWrapper.style.setProperty("display", "none"), 300);
  };

  onNestedFilterToggle = (event) => {
    if (event.target.matches('.kp-fragrance-type-options > details')) {
      this.accordions && this.accordions.setContentHeight();
    }
  };

  initAccordions = () => {
    if (this.nestedDetailsWrapper) {
      this.nestedDetailsWrapper.removeEventListener('toggle', this.onNestedFilterToggle, true);
    }
    this.accordions && this.accordions.destroy();
    const { filtersWrapper } = this.domNodes;
    if (!filtersWrapper) return;
    if (typeof MinimogLibs?.Accordion !== "function") {
      filtersWrapper.style.opacity = "1";
      filtersWrapper.querySelectorAll(".m-accordion--item-content").forEach((content) => {
        content.dataset.kpMissingAccordion = "true";
        content.style.maxHeight = "none";
        content.style.opacity = "1";
        content.classList.remove("m:overflow-hidden");
      });
      return;
    }
    filtersWrapper.querySelectorAll('[data-kp-missing-accordion]').forEach((content) => {
      content.style.removeProperty("max-height");
      content.style.removeProperty("opacity");
      delete content.dataset.kpMissingAccordion;
    });
    filtersWrapper.classList.remove("acc-initialized");
    this.accordions = new MinimogLibs.Accordion(filtersWrapper, {
      presetContentHeight: window.innerWidth > 1280 && this.filtersPosition !== "fixed",
      callback: () => filtersWrapper.style.setProperty('opacity', '1')
    });
    // Native details toggles do not bubble; keep the containing filter's height in sync.
    this.nestedDetailsWrapper = filtersWrapper;
    filtersWrapper.addEventListener('toggle', this.onNestedFilterToggle, true);
  };

  // Keyboard: Escape closes the open panel, focus moves into it and returns to the button that opened it.
  trapPanel = (panel, close) => {
    this.releasePanel();
    this.panelOpener = document.activeElement;
    this.panelKeydown = (e) => {
      if (e.key === "Escape") { e.preventDefault(); close(); return; }
      if (e.key !== "Tab") return;
      const items = [...panel.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select,textarea,summary,[tabindex]:not([tabindex="-1"])')].filter((el) => el.getClientRects().length);
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", this.panelKeydown);
    requestAnimationFrame(() => panel.querySelector('button,a[href],input:not([type="hidden"])')?.focus({ preventScroll: true }));
  };

  releasePanel = () => {
    if (!this.panelKeydown) return;
    document.removeEventListener("keydown", this.panelKeydown);
    this.panelKeydown = null;
    const opener = this.panelOpener;
    this.panelOpener = null;
    if (opener && opener.isConnected) opener.focus({ preventScroll: true });
  };

  openSidebarFilter = () => {
    const { sidebar, sidebarContent, section } = this.domNodes;
    if (window.innerWidth < 1280 || sidebar.dataset.type === "fixed") this.trapPanel(sidebarContent, this.closeSidebarFilter);
    sidebar.style.display = "block";
    window.requestAnimationFrame(() => {
      sidebar.style.setProperty("--m-bg-opacity", "0.5");
      sidebarContent.style.setProperty("--m-translate-x", "0");
      this.accordions && this.accordions.setContentHeight();
    });
    section.classList.add("sidebar-open");
  };

  closeSidebarFilter = () => {
    const { sidebar, sidebarContent, section } = this.domNodes;
    this.releasePanel();
    section.classList.remove("sidebar-open");
    if (window.innerWidth < 1280 || sidebar.dataset.type === "fixed") {
      sidebarContent.style.setProperty("--m-translate-x", "-100%");
      sidebar.style.removeProperty("--m-bg-opacity");
      setTimeout(() => sidebar.style.removeProperty("display"), 300);
    }
  };

  toggleView = (col, persist = false) => {
    if (!col || this.view === "search") return;
    const { cols, productContainer } = this.domNodes;
    if (!productContainer) return;
    const activeCol = this.activeCol || this.initialCol;
    if (this.showColSwitchers) {
      cols.forEach(button => {
        const selected = Number(button.dataset.column) === col;
        button.classList.toggle("active", selected);
        button.setAttribute("aria-pressed", String(selected));
      });
    }

    if (!productContainer.classList.contains(`m-cols-${col}`)) {
      productContainer.classList.remove(`m-cols-${activeCol}`);
      productContainer.classList.add(`m-cols-${col}`);
    }

    if (persist) {
      this.deviceColumns = this.deviceColumns || {};
      this.deviceColumns[this.getDeviceByScreenSize()] = col;
      // The shopper chose a column count: stop collection.css widening the 4-column grid on wide screens.
      productContainer.classList.add("kp-cols-set");
    }
    this.activeCol = col;
  };

  updateViewByScreen = () => {
    const device = this.getDeviceByScreenSize();
    // Wide screens (>=1600px) show one more column than the template's desktop setting, matching collection.css.
    const base = device === "wide" ? this.initialCol + 1 : this.initialCol;
    const preferred = this.deviceColumns?.[device] || Math.min(base, this.MAX_COL_BY_SCREEN_SIZE[device]);
    if (preferred !== this.activeCol) this.toggleView(preferred, false);
  };

  getDeviceByScreenSize = () => {
    if (window.innerWidth < 768) return "mobile";
    if (window.innerWidth < 1024) return "tablet";
    if (window.innerWidth >= 1600) return "wide";
    return "desktop";
  };

  initLoadMore = () => {
    if (this.paginationType === "paginate" || this.totalPages <= 1 || this.view === "search") return;

    const { loadMoreBtn } = this.domNodes;
    if (!loadMoreBtn) return;
    loadMoreBtn.addEventListener("click", this.loadMoreProducts);

    // Fetch the next page while the shopper scrolls towards the button, so a click adds the products at once.
    // Products that arrived ~650 ms after the click pushed the footer while it was on screen, outside the
    // 500 ms input grace period: Clarity CLS 0.35–0.58 on Artisan collections (Oct 2026), 0.55 in a live probe.
    if ("IntersectionObserver" in window) {
      this.prefetchObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.prefetchNextPage();
        },
        { rootMargin: "0px 0px 1200px 0px" }
      );
      this.prefetchObserver.observe(loadMoreBtn);
    }

    if (this.paginationType === "infinite") {
      this.infiniteLoadingObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio === 1) this.loadMoreProducts();
          });
        },
        { threshold: 1 }
      );

      this.infiniteLoadingObserver.observe(loadMoreBtn);
    }
  };

  prefetchNextPage = () => {
    const nextPage = this.currentPage + 1;
    if (typeof fetchSection !== "function" || this.loadingMore || nextPage > this.totalPages) return;
    fetchSection(this.sectionId, { fromCache: true, params: { page: nextPage } }).catch(() => {});
  };

  loadMoreProducts = () => {
    if (typeof fetchSection !== "function") return;
    const nextPage = this.currentPage + 1;
    if (this.loadingMore || nextPage > this.totalPages) return;

    const { productContainer, loadMoreBtn } = this.domNodes;
    const stillCurrent = () => productContainer.isConnected && this.domNodes.productContainer === productContainer;
    this.loadingMore = true;
    this.toggleLoading(true);
    window.KPTheme?.track('collection_load_more_attempt', { page: nextPage });
    // Paint the tap (spinner) before parsing and adding the cards: building 48 cards inside the tap took
    // ~270 ms on a mid-range phone (INP ~420 ms). The prefetched cards still land well inside the 500 ms
    // input window, so the footer moving down is not counted as a layout shift.
    const afterPaint = new Promise((resolve) => {
      if (typeof requestAnimationFrame !== "function") return resolve();
      requestAnimationFrame(() => (typeof setTimeout === "function" ? setTimeout(resolve, 0) : resolve()));
    });
    return afterPaint
      .then(() => fetchSection(this.sectionId, { fromCache: true, params: { page: nextPage } }))
      .then((productGridHTML) => {
        if (!stillCurrent()) return;
        const productNodes = productGridHTML.querySelectorAll("[data-product-container] .m-product-item");
        productNodes.forEach((prodNode) => productContainer.appendChild(prodNode));
        this.initProducts();
        this.currentPage = nextPage;
        const pageLinks = productContainer.closest('.facest-filters-section')?.querySelector('.kp-fragrance-pages');
        const nextPageLinks = productGridHTML.querySelector('.kp-fragrance-pages');
        if (pageLinks && nextPageLinks) pageLinks.replaceWith(nextPageLinks);
        document.dispatchEvent(new CustomEvent('kp:collection-products-added'));
        window.KPTheme?.track('collection_load_more_success', { page: nextPage, product_count: productNodes.length });
      })
      .catch((err) => {
        window.KPTheme?.track('collection_load_more_failure', { page: nextPage });
        console.error('Failed to load more products.', err);
      })
      .finally(() => {
        if (!stillCurrent()) return;
        MinimogEvents.emit("ON_PRODUCT_LIST_UPDATED");
        this.loadingMore = false;
        this.toggleLoading(false);
        if (this.currentPage >= this.totalPages) {
          loadMoreBtn.parentNode.remove();
          this.infiniteLoadingObserver && this.infiniteLoadingObserver.unobserve(loadMoreBtn);
          this.prefetchObserver?.unobserve(loadMoreBtn);
        } else if (this.prefetchObserver) {
          // Observing again reports the button's current position, so the page after this one is fetched too.
          this.prefetchObserver.unobserve(loadMoreBtn);
          this.prefetchObserver.observe(loadMoreBtn);
        }
      });
  };

  initProducts = () => {
    MinimogTheme.CompareProduct && MinimogTheme.CompareProduct.setCompareButtonsState();
    MinimogTheme.Wishlist && MinimogTheme.Wishlist.setWishlistButtonsState();
  };

  toggleLoading = (loading) => {
    const { loadMoreBtn } = this.domNodes;
    if (loadMoreBtn) {
      const method = loading ? "add" : "remove";
      loadMoreBtn.classList[method]("m-spinner-loading");
    }
  };

  handleStickyToolbar() {
    if (this.toolbarScrollHandler) window.removeEventListener('scroll', this.toolbarScrollHandler);
    const toolbar = this.domNodes.toolbar;
    if (!toolbar) return;
    const extraSpace = toolbar && toolbar.offsetTop;
    let lastScroll = 0;
    this.toolbarScrollHandler = () => {
      if (MinimogTheme.config.mqlMobile) {
        const currentScroll = window.scrollY;
        if (currentScroll <= extraSpace) {
          toolbar.classList.remove("scroll-up", "m-collection-toolbar--sticky");
          return;
        }
        toolbar.classList.add("m-collection-toolbar--sticky");
        if (
          currentScroll > toolbar.offsetHeight + extraSpace &&
          currentScroll > lastScroll &&
          !toolbar.classList.contains("scroll-down")
        ) {
          toolbar.classList.remove("scroll-up");
          toolbar.classList.add("scroll-down");
        } else if (currentScroll < lastScroll && toolbar.classList.contains("scroll-down")) {
          toolbar.classList.remove("scroll-down");
          toolbar.classList.add("scroll-up");
        }
        lastScroll = currentScroll;
      } else {
        toolbar.classList.remove("scroll-up", "m-collection-toolbar--sticky", "scroll-down");
      }
    };
    window.addEventListener('scroll', this.toolbarScrollHandler, { passive: true });
  }
}
